import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaEnvelope, FaPhone, FaGlobe, FaChevronRight } from "react-icons/fa";
import Header from "./Header";
import CommonFooterNav from "../Common/CommonFooterNav";
import { getCachedData, setCachedData } from "../utils/apiCache";

const SchoolInfo = () => {
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFullAbout, setShowFullAbout] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSchoolAutomation = async () => {
      const cacheKey = "school_info_automation_data";
      const cached = getCachedData(cacheKey);
      if (cached) {
        setSchoolData(cached);
        setLoading(false);
      }
      try {
        const authtoken = localStorage.getItem("token");
        const response = await fetch("https://api.mirai.healthcare/api/head/school-rule/school-automation", {
          method: "GET",
          headers: {
            Accept: "application/json",
            ...(authtoken ? { "x-auth": authtoken } : {})
          }
        });
        if (response.ok) {
          const data = await response.json();
          const automationInfo = Array.isArray(data) ? data[0] : (data.data?.[0] || data);
          setSchoolData(automationInfo);
          setCachedData(cacheKey, automationInfo);
        }
      } catch (err) {
        console.error("Error fetching school automation info:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSchoolAutomation();
  }, []);

  const schoolImage = schoolData?.schoolImage || "https://images.unsplash.com/photo-1580582932707-520aed937b7b?auto=format&fit=crop&w=800&q=80";
  const schoolLogo = schoolData?.schoolLogo;

  // Images for carousel
  const carouselImages = [
    schoolImage,
    "https://images.unsplash.com/photo-1541829070764-84a7d30dd3f3?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=800&q=80"
  ];

  const fullAboutText = schoolData?.aboutUs || `"Foundation of Human Potential" is our vision and Mission. All our efforts put-in at The Adhyyan School aims to evolve our students' into better professionals and even better human beings... The Adhyyan School aims at enriching the lives of its students', their families and community through dedication and excellence.`;

  const truncatedAboutText = fullAboutText.length > 220
    ? fullAboutText.substring(0, 220) + "..."
    : fullAboutText;

  const websiteUrlClean = schoolData?.websiteUrl
    ? schoolData.websiteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')
    : "theadhyyan.edu.in";

  return (
    <div className="page-wrapper school-info-page min-vh-100 position-relative" style={{ background: "#f4f6fc", paddingBottom: "70px" }}>
      {/* Top Header on White Bar for perfect contrast & visibility */}
      <div className="bg-white border-bottom shadow-sm position-relative" style={{ zIndex: 10 }}>
        <Header />
      </div>

      {/* Blue Hero Banner Container */}
      <div className="school-info-hero position-relative" style={{ background: "linear-gradient(180deg, #0288d1 0%, #03a9f4 200px, #e0f2fe 280px, #fffde7 100%)" }}>
        {/* Top Blue Header Title & Illustration */}
        <div className="school-info-header text-white pt-3 pb-4 px-3 text-center position-relative">
          <h4 className="fw-bold mb-1 text-white text-center" style={{ fontSize: "20px" }}>
            School Information
          </h4>

          {/* School Vector Header Banner */}
          <div className="my-2 text-center">
            <svg width="220" height="90" viewBox="0 0 300 120" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: "80%" }}>
              {/* Background clouds */}
              <path d="M40 35 C50 25 70 25 80 35 C90 25 110 25 120 35 C130 35 135 45 130 50 L35 50 C30 45 35 35 40 35 Z" fill="#ffffff" opacity="0.3" />
              <path d="M190 25 C200 15 220 15 230 25 C240 15 260 15 270 25 C280 25 285 35 280 40 L185 40 C180 35 185 25 190 25 Z" fill="#ffffff" opacity="0.3" />

              {/* School Building */}
              <rect x="90" y="45" width="120" height="60" fill="#e57373" rx="4" />
              <polygon points="150,15 80,45 220,45" fill="#c62828" />
              <rect x="135" y="70" width="30" height="35" fill="#5d4037" rx="2" />
              <circle cx="150" cy="35" r="10" fill="#ffffff" />
              <circle cx="150" cy="35" r="7" fill="#ffe082" />
              {/* Windows */}
              <rect x="105" y="55" width="18" height="18" fill="#e0f7fa" rx="2" />
              <rect x="177" y="55" width="18" height="18" fill="#e0f7fa" rx="2" />
              <rect x="105" y="80" width="18" height="18" fill="#e0f7fa" rx="2" />
              <rect x="177" y="80" width="18" height="18" fill="#e0f7fa" rx="2" />

              {/* Trees */}
              <circle cx="65" cy="70" r="18" fill="#81c784" />
              <rect x="62" y="85" width="6" height="20" fill="#5d4037" />
              <circle cx="235" cy="70" r="18" fill="#81c784" />
              <rect x="232" y="85" width="6" height="20" fill="#5d4037" />

              {/* Little Students & Parents */}
              <circle cx="115" cy="95" r="6" fill="#ffb74d" />
              <path d="M109 101 L121 101 L118 115 L112 115 Z" fill="#1e88e5" />
              <circle cx="185" cy="95" r="6" fill="#ffb74d" />
              <path d="M179 101 L191 101 L188 115 L182 115 Z" fill="#43a047" />
            </svg>
          </div>
        </div>

        {/* Main Content Container */}
        <div className="container-fluid px-3 position-relative" style={{ marginTop: "-10px", zIndex: 2, paddingBottom: "30px" }}>
          {loading ? (
            <div className="text-center py-5 bg-white rounded-4 shadow-sm">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2 small">Fetching School Details...</p>
            </div>
          ) : (
            <>
              {/* Card 1: School Image Banner Carousel */}
              <div className="card border-0 shadow-sm rounded-4 mb-3 overflow-hidden bg-white">
                <div className="card-body p-2 text-center position-relative">
                  <img
                    src={carouselImages[activeSlide]}
                    alt="School Campus"
                    className="w-100 rounded-4"
                    style={{ height: "200px", objectFit: "cover", transition: "all 0.4s ease" }}
                  />

                  {/* Carousel Dots */}
                  <div className="d-flex align-items-center justify-content-center gap-2 mt-2 pb-1">
                    {carouselImages.map((_, index) => (
                      <span
                        key={index}
                        onClick={() => setActiveSlide(index)}
                        style={{
                          width: activeSlide === index ? "12px" : "8px",
                          height: "8px",
                          borderRadius: "10px",
                          backgroundColor: activeSlide === index ? "#0288d1" : "#b0bec5",
                          cursor: "pointer",
                          transition: "all 0.3s ease"
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Card 2: About School */}
              <div className="card border-0 shadow-sm rounded-4 mb-3 bg-white">
                <div className="card-body p-3">
                  <h6 className="fw-bold text-dark mb-3" style={{ fontSize: "16px" }}>
                    About School
                  </h6>
                  <p className="text-secondary small mb-2" style={{ lineHeight: "1.6", fontSize: "13.5px" }}>
                    {showFullAbout ? fullAboutText : truncatedAboutText}
                  </p>
                  {fullAboutText.length > 220 && (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none fw-semibold small"
                      onClick={() => setShowFullAbout(!showFullAbout)}
                      style={{ color: "#0288d1", fontSize: "13px" }}
                    >
                      {showFullAbout ? "Read Less" : "Read More"}
                    </button>
                  )}
                </div>
              </div>

              {/* Card 3: School Info Details */}
              <div className="card border-0 shadow-sm rounded-4 mb-4 bg-white">
                <div className="card-body p-3">
                  {/* Principal Name */}
                  <div className="mb-3">
                    <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "15px" }}>
                      Principal Name
                    </h6>
                    <p className="text-secondary small mb-0" style={{ fontSize: "14px" }}>
                      {schoolData?.principalName || "Mr Sunil kumar Verma"}
                    </p>
                  </div>

                  {/* Contact Address */}
                  <div className="mb-3">
                    <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "15px" }}>
                      Contact Address
                    </h6>
                    <p className="text-secondary small mb-0" style={{ lineHeight: "1.5", fontSize: "13.5px" }}>
                      {schoolData?.schoolAddress || "Plot No: E-4, Pocket-A, Sector 4C, Shatabdi Nagar Meerut 250103 Uttar Pradesh – India"}
                    </p>
                  </div>

                  {/* School Email */}
                  <div className="mb-3">
                    <h6 className="fw-bold text-dark mb-2" style={{ fontSize: "15px" }}>
                      School Email
                    </h6>
                    <div className="d-flex align-items-center p-3 rounded-4 border bg-white shadow-xs">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-3 text-white me-3"
                        style={{ width: "36px", height: "36px", backgroundColor: "#1e293b" }}
                      >
                        <FaEnvelope size={16} />
                      </div>
                      <span className="text-dark fw-medium small" style={{ fontSize: "13.5px", wordBreak: "break-all" }}>
                        {schoolData?.email || "info@theadhyyan.edu.in"}
                      </span>
                    </div>
                  </div>

                  {/* Contact Phones */}
                  <div className="mb-3">
                    <div className="row g-2">
                      {/* Phone Icon Box 1 */}
                      <div className="col-4">
                        <div className="d-flex align-items-center justify-content-center p-3 rounded-4 border bg-white shadow-xs h-100">
                          <FaPhone className="text-dark" size={18} />
                        </div>
                      </div>
                      {/* Phone Number Box 2 */}
                      <div className="col-8">
                        <div className="d-flex align-items-center p-3 rounded-4 border bg-white shadow-xs h-100">
                          <FaPhone className="text-dark me-2 flex-shrink-0" size={16} />
                          <span className="text-dark fw-medium small" style={{ fontSize: "13.5px" }}>
                            {schoolData?.contact_number || schoolData?.landline_mobile || "+919012777716"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* School WebPage */}
                  <div>
                    <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "15px" }}>
                      School WebPage
                    </h6>
                    <span
                      className="fw-medium small text-muted"
                      style={{ fontSize: "14px" }}
                    >
                      {websiteUrlClean}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Common Bottom Navigation Bar */}
      <CommonFooterNav />
    </div>
  );
};

export default SchoolInfo;
